import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../../config/axios';
import { RefreshCw } from 'lucide-react';

const CAPTCHA_LENGTH = 5;

const isAbortError = (err) =>
  err?.code === 'ERR_CANCELED' ||
  err?.name === 'CanceledError' ||
  err?.name === 'AbortError' ||
  (typeof axios.isCancel === 'function' && axios.isCancel(err));

/**
 * 5-character alphanumeric captcha for public shipment tracking.
 * Parent enables Track only when onVerified(true) after a matching answer.
 */
const TrackCaptcha = ({
  answer,
  onAnswerChange,
  onTokenChange,
  onVerified,
  disabled = false,
}) => {
  const [question, setQuestion] = useState('');
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const questionRef = useRef('');
  const tokenRef = useRef('');
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef(null);
  const isActiveRef = useRef(true);

  // Keep latest callbacks without retriggering load on every parent render
  const onAnswerChangeRef = useRef(onAnswerChange);
  const onTokenChangeRef = useRef(onTokenChange);
  const onVerifiedRef = useRef(onVerified);
  onAnswerChangeRef.current = onAnswerChange;
  onTokenChangeRef.current = onTokenChange;
  onVerifiedRef.current = onVerified;

  const clearParentCaptcha = useCallback(() => {
    onAnswerChangeRef.current('');
    onTokenChangeRef.current('');
    if (typeof onVerifiedRef.current === 'function') onVerifiedRef.current(false);
  }, []);

  const loadChallenge = useCallback(async () => {
    // Drop any in-flight challenge so a late response cannot overwrite a newer one
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const requestId = ++requestIdRef.current;

    setLoading(true);
    setReady(false);
    setError('');
    // Clear parent auth immediately so Track cannot use a stale token/answer,
    // but keep the visible code until the new one arrives (avoids blank flash).
    clearParentCaptcha();

    try {
      // Cache-bust so refresh always gets a new code (browsers cache plain GETs)
      const response = await axios.get('/tracking/challenge', {
        params: { _ts: Date.now() },
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      // Ignore stale responses (refresh / Strict Mode remount / unmount)
      if (!isActiveRef.current || requestId !== requestIdRef.current) return;

      const token = response.data?.token;
      const code = response.data?.question;

      if (response.data?.success && token && code) {
        const normalized = String(code).trim().toUpperCase();
        questionRef.current = normalized;
        tokenRef.current = token;
        setQuestion(normalized);
        setReady(true);
        // Set token + clear answer together so displayed code always matches token
        onAnswerChangeRef.current('');
        onTokenChangeRef.current(token);
        if (typeof onVerifiedRef.current === 'function') onVerifiedRef.current(false);
      } else {
        questionRef.current = '';
        tokenRef.current = '';
        setQuestion('');
        setReady(false);
        clearParentCaptcha();
        setError('Could not load security code. Please refresh.');
      }
    } catch (err) {
      if (isAbortError(err)) return;
      if (!isActiveRef.current || requestId !== requestIdRef.current) return;

      questionRef.current = '';
      tokenRef.current = '';
      setQuestion('');
      setReady(false);
      clearParentCaptcha();

      const status = err.response?.status;
      const msg =
        status === 404
          ? 'Security service unavailable. Restart the backend server, then refresh this page.'
          : 'Could not load security code. Please refresh.';
      setError(msg);
    } finally {
      if (isActiveRef.current && requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [clearParentCaptcha]);

  useEffect(() => {
    isActiveRef.current = true;
    loadChallenge();

    return () => {
      // Prevent late responses from this instance from updating parent token/answer
      isActiveRef.current = false;
      requestIdRef.current += 1;
      abortControllerRef.current?.abort();
    };
  }, [loadChallenge]);

  const handleAnswerChange = (raw) => {
    const next = String(raw || '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, CAPTCHA_LENGTH)
      .toUpperCase();
    onAnswerChangeRef.current(next);

    const matched =
      Boolean(questionRef.current) &&
      Boolean(tokenRef.current) &&
      next.length === CAPTCHA_LENGTH &&
      next === questionRef.current;
    if (typeof onVerifiedRef.current === 'function') onVerifiedRef.current(matched);
  };

  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <div className="flex items-center gap-2 min-w-0">
        <div
          className="
            relative shrink-0 select-none overflow-hidden
            w-[5.75rem] sm:w-[6.5rem] h-11
            rounded-lg border border-purple-300 bg-white
            flex items-center justify-center
          "
          aria-label="Security code"
          aria-live="polite"
        >
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none opacity-30"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <line x1="0" y1="8" x2="100%" y2="36" stroke="#7c3aed" strokeWidth="1" />
            <line x1="0" y1="28" x2="100%" y2="10" stroke="#a78bfa" strokeWidth="1" />
            <line x1="10%" y1="0" x2="85%" y2="100%" stroke="#c4b5fd" strokeWidth="1" />
          </svg>
          <span
            className="
              relative z-[1] font-extrabold tracking-[0.2em] text-base sm:text-[17px]
              text-gray-900
            "
            style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
          >
            {loading && !question ? '·····' : (question || '·····')}
          </span>
        </div>

        <button
          type="button"
          onClick={loadChallenge}
          disabled={disabled || loading}
          aria-label="Refresh security code"
          title="New code"
          className="
            inline-flex items-center justify-center
            w-11 h-11 shrink-0 rounded-lg
            border border-purple-100 bg-white text-purple-700
            hover:bg-purple-50 transition-colors
            disabled:opacity-50
          "
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>

        <input
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={CAPTCHA_LENGTH}
          value={answer}
          disabled={disabled || loading || !ready || !question}
          onChange={(e) => handleAnswerChange(e.target.value)}
          placeholder="Enter Captcha"
          aria-label="Enter security code"
          className="
            login-input
            w-[5.5rem] sm:w-36 h-11 px-2.5
            text-sm text-center tracking-wider uppercase
            border border-purple-100 rounded-lg bg-white
            focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200
            transition-all placeholder:text-gray-400 placeholder:tracking-normal placeholder:normal-case
            disabled:opacity-50
          "
        />
      </div>

      {error && <p className="text-xs text-red-600 max-w-[18rem] sm:max-w-none">{error}</p>}
    </div>
  );
};

export default TrackCaptcha;
